/**
 * "Erase everything stored by this site", across every tab that has this site
 * open (backlog:3704).
 *
 * THE DEFECT. `sessionStorage` is scoped to one browsing context by the Web
 * Storage API itself, not shared across tabs the way `localStorage` is. The
 * sweep in `eraseStorage.ts` reads `window.sessionStorage`, which is the
 * CALLING tab's own area and nothing else, so a recent-message entry held in
 * a second open tab survived a run that reported "Everything stored by this
 * site has been erased." No API lets one tab reach another's area, so the
 * only way to make that sentence true is to ask the other tabs to do it
 * themselves and wait for them to say they did.
 *
 * THE ROUND. Every tab installs {@link installErasePeer} at boot. The tab
 * whose button was pressed posts one erase request on a same-origin
 * `BroadcastChannel`, each peer sweeps ITS OWN `sessionStorage` and posts
 * back the counts, and the initiator folds those counts into the same
 * found/removed/reachable/unreachable accounting the local sweep already
 * used. So a peer that could not read its own area turns the outcome
 * partial, exactly as a local area that refuses to open does, rather than
 * being silently rounded up into a total claim.
 *
 * WHAT CROSSES THE CHANNEL. Four numbers, one literal type tag and one round
 * id. Never a key name, never a stored value, never a byte of a message. The
 * channel is same-origin and same-browser by construction (it reaches no
 * network at all), but a privacy tool should not be shipping message content
 * between contexts even locally, so the protocol is built so there is nothing
 * to leak; `eraseChannel.test.ts` holds the posted payload to exactly that
 * shape.
 *
 * WHY PEERS SWEEP ONLY `sessionStorage`. `localStorage` and Cache Storage are
 * shared by the whole origin, so the initiator's own sweep already reaches
 * every tab's copy. A peer sweeping them too would race the initiator for the
 * same single share payload, and the loser's `delete()` returning false would
 * be reported as a key that was found and could not be removed: a false
 * "partial" over a wipe that worked.
 *
 * THE HONEST LIMIT, stated because the button's sentence is a factual claim.
 * A tab whose main thread is busy when the round runs (mid-parse on a large
 * message) may not answer inside {@link ERASE_ACK_WINDOW_MS}, and there is no
 * API that tells the initiator how many tabs exist, so an unanswered tab is
 * invisible and cannot be counted as unreachable. That is a receipt gap, not
 * an erasure gap: `BroadcastChannel` queues the message, so the busy tab still
 * runs its sweep and still reloads, just after the initiator has printed its
 * count. The one case that stays open is a tab whose script is dead
 * altogether, which no protocol can reach.
 */
import { errorLog } from "../utils/errorLog";
import {
  emptySweep,
  eraseOwnedStorage,
  foldResult,
  sweepOwnSessionStorage,
  type EraseSweepResult,
} from "./eraseStorage";

/**
 * The channel name, under the `emlx:` namespace every other name this origin
 * owns already uses. Not a storage key: nothing is persisted under it, so it
 * is not in `STORAGE_KEYS`.
 */
export const ERASE_CHANNEL_NAME = "emlx:erase";

/** The protocol revision, so a tab left open across a deploy can be told apart. */
export const ERASE_PROTOCOL = 1;

/**
 * How long the initiator waits for acknowledgements before printing its
 * count. Long enough for an idle tab to answer (the work is a synchronous
 * walk over one storage area, microseconds), short enough that a deliberate
 * two-press action still feels immediate. It runs concurrently with the local
 * Cache Storage sweep, so it usually costs nothing at all.
 */
export const ERASE_ACK_WINDOW_MS = 300;

/**
 * How long a peer waits before reloading itself after a clean sweep. The same
 * delay the initiating tab gives itself, for the same reason: the reload is
 * how in-memory state is dropped, and it should not race the sweep that
 * earned it.
 */
export const PEER_RELOAD_DELAY_MS = 600;

/** Ask every other tab to erase its own tab-scoped storage. */
interface EraseRequest {
  v: typeof ERASE_PROTOCOL;
  type: "erase";
  /** Ties acknowledgements to this round, so two tabs erasing at once do not mix. */
  id: string;
}

/** One peer's report: counts only, from its own `sessionStorage`. */
interface EraseAck {
  v: typeof ERASE_PROTOCOL;
  type: "erased";
  id: string;
  found: number;
  removed: number;
  /** 1 if that tab could read its own area, 0 if the area refused to open. */
  reachable: number;
}

type EraseMessage = EraseRequest | EraseAck;

/**
 * Rounds THIS context started.
 *
 * A `BroadcastChannel` does not deliver to the object that posted, but it does
 * deliver to every OTHER object with that name, including the peer listener
 * this same tab installed at boot. Left alone, the initiating tab answers its
 * own request: it re-sweeps an area it has already swept, adds itself to its
 * own reachable count as though it were a second tab, and — the one that
 * showed — arms a peer reload on ITS OWN clean sweep, which fires whatever the
 * round as a whole came to. That reload took the page out from under the
 * receipt, and it would have taken a PARTIAL sweep's on-screen warning with
 * it, which is the one outcome that must stay put.
 */
const ownRounds = new Set<string>();

/** Fallback round ids when `crypto.randomUUID` is missing; only has to be unique. */
let roundCounter = 0;

function roundId(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `erase-${++roundCounter}-${Date.now()}`;
}

/**
 * Open the channel, or report that this browser has none.
 *
 * `BroadcastChannel` ships in every browser this app targets, so `undefined`
 * here means an old engine or a context that has walled it off. Either way
 * the other tabs cannot be asked, which is an unreachable area and is
 * reported as one, not quietly treated as "there are no other tabs".
 */
function openChannel(): BroadcastChannel | undefined {
  if (typeof BroadcastChannel === "undefined") return undefined;
  try {
    return new BroadcastChannel(ERASE_CHANNEL_NAME);
  } catch (err) {
    errorLog("eraseChannel.open", err, {});
    return undefined;
  }
}

/** True for a message this protocol recognises, at this revision. */
function isEraseMessage(data: unknown): data is EraseMessage {
  if (!data || typeof data !== "object") return false;
  const m = data as Partial<EraseMessage>;
  return (
    m.v === ERASE_PROTOCOL &&
    typeof m.id === "string" &&
    (m.type === "erase" || m.type === "erased")
  );
}

/**
 * Answer erase rounds started by other tabs, for as long as this tab lives.
 *
 * Installed once at boot (`main.tsx`) rather than by the Settings view,
 * because the tab holding the message that needs erasing is precisely the one
 * that is NOT looking at Settings. Returns a disposer so a test can take it
 * back down; the app never calls it.
 *
 * A peer that swept its own area clean reloads itself, on the initiator's own
 * rule and for the initiator's own reason: without it the tab keeps the wiped
 * message on screen and in memory, and the next state change writes its
 * recent-message entry straight back, undoing the erase that was just
 * acknowledged.
 */
export function installErasePeer(): () => void {
  const channel = openChannel();
  if (!channel) return () => {};
  const onMessage = (ev: MessageEvent) => {
    const msg: unknown = ev.data;
    if (!isEraseMessage(msg) || msg.type !== "erase") return;
    // Not our own round: the tab that pressed the button sweeps its own areas
    // through `eraseEverywhere` and reloads on the WHOLE outcome, not on this
    // handler's partial view of it.
    if (ownRounds.has(msg.id)) return;
    // Own area only: see the file header on why a peer must not touch the
    // origin-shared stores the initiator is sweeping at the same moment.
    const swept = sweepOwnSessionStorage();
    const ack: EraseAck = {
      v: ERASE_PROTOCOL,
      type: "erased",
      id: msg.id,
      found: swept.found,
      removed: swept.removed,
      reachable: swept.reachable,
    };
    try {
      channel.postMessage(ack);
    } catch (err) {
      // The initiator will simply not hear from this tab. It cannot know the
      // tab exists, so there is nothing to report; the sweep still happened.
      errorLog("eraseChannel.ack", err, {});
    }
    if (swept.reachable === 1 && swept.found === swept.removed) {
      window.setTimeout(() => window.location.reload(), PEER_RELOAD_DELAY_MS);
    }
  };
  channel.addEventListener("message", onMessage);
  return () => {
    channel.removeEventListener("message", onMessage);
    channel.close();
  };
}

/**
 * Run one erase-and-acknowledge round and report what the other tabs did.
 *
 * The round itself counts as one more area read, which is what keeps the
 * ordinary single-tab case honest: asking and hearing nothing back is an
 * area that answered clean (`found` 0), not an area that refused. Each peer
 * that answers then contributes its own area on top, reachable or not, so one
 * tab that could not read its own `sessionStorage` turns the whole outcome
 * partial.
 */
export async function sweepPeerTabs(): Promise<EraseSweepResult> {
  const channel = openChannel();
  // No channel: other tabs may exist and cannot be asked. Unreachable area.
  if (!channel) return { found: 0, removed: 0, reachable: 0, unreachable: 1 };

  const id = roundId();
  ownRounds.add(id);
  const total: EraseSweepResult = { found: 0, removed: 0, reachable: 1, unreachable: 0 };
  const onMessage = (ev: MessageEvent) => {
    const msg: unknown = ev.data;
    if (!isEraseMessage(msg) || msg.type !== "erased" || msg.id !== id) return;
    total.found += msg.found;
    total.removed += msg.removed;
    total.reachable += msg.reachable;
    if (msg.reachable === 0) total.unreachable++;
  };
  channel.addEventListener("message", onMessage);
  try {
    const request: EraseRequest = { v: ERASE_PROTOCOL, type: "erase", id };
    channel.postMessage(request);
  } catch (err) {
    // Asking failed, so the other tabs are exactly as unreached as they are
    // with no channel at all, and are reported the same way.
    errorLog("eraseChannel.post", err, {});
    ownRounds.delete(id);
    channel.removeEventListener("message", onMessage);
    channel.close();
    return { found: 0, removed: 0, reachable: 0, unreachable: 1 };
  }
  await new Promise<void>((resolve) => window.setTimeout(resolve, ERASE_ACK_WINDOW_MS));
  ownRounds.delete(id);
  channel.removeEventListener("message", onMessage);
  channel.close();
  return total;
}

/**
 * The whole erase: this tab's three stores plus every other tab's own
 * `sessionStorage`, as one result the button's sentence is earned from.
 *
 * The two halves run concurrently because they touch disjoint stores, so the
 * acknowledgement window overlaps the local Cache Storage sweep instead of
 * being added to it.
 */
export async function eraseEverywhere(): Promise<EraseSweepResult> {
  const [own, peers] = await Promise.all([eraseOwnedStorage(), sweepPeerTabs()]);
  return foldResult(foldResult(emptySweep(), own), peers);
}
